import { signal, computed, batch, effect } from '@preact/signals';
import type { Chapter, EpisodeDto, LiveItemDto } from '../../shared/types';
import { api, beaconProgress } from '../api';
import { preloadUpcoming } from '../lib/preload';
import {
  CAST_MAX_RATE,
  castConnected,
  castLoad,
  castPause,
  castPlay,
  castSeek,
  castSetRate,
  onCastStatus,
} from './cast';
import {
  hasNative,
  INSECURE_ENCLOSURE_MESSAGE,
  isInsecureEnclosureUrl,
  onNativeEvent,
  whenNative,
} from './native';
import type { CastStatus } from './native';
import { queueItems } from './queue';
import { settings } from './session';

/**
 * Player state lives at module scope, not inside a component.
 *
 * The single `<audio>` element is mounted once at the app root, outside the router outlet,
 * so navigating between views never unmounts it and playback is continuous. Everything the
 * UI needs to render is a signal, so any component can subscribe without prop drilling.
 *
 * There are two players behind these signals, not one. When the Android shell has a Chromecast
 * session up, the receiver is playing and the `<audio>` element is silent: commands go out over
 * the bridge and position, duration and playing state come back as pushed frames. Every component
 * reads the same signals either way and none of them knows which player is live.
 */

export const currentEpisode = signal<EpisodeDto | null>(null);
export const isPlaying = signal(false);
export const isBuffering = signal(false);
export const isLive = signal(false);
export const liveItem = signal<LiveItemDto | null>(null);
export const positionSec = signal(0);
export const durationSec = signal(0);
export const playbackRate = signal(1);
export const playbackError = signal<string | null>(null);
export const nowPlayingOpen = signal(false);
export const liveChatOpen = signal(false);
export const activeLiveChat = signal<{ feedId: number; guid: string; title: string } | null>(null);
export const chapters = signal<Chapter[]>([]);
export const sleepTimerEndsAt = signal<number | null>(null);
export const sleepTimerAtEpisodeEnd = signal(false);

const initialVolume = (() => {
  try {
    const saved = localStorage.getItem('podcastie:volume');
    if (saved !== null) {
      const v = Number.parseFloat(saved);
      if (Number.isFinite(v) && v >= 0 && v <= 1) return v;
    }
  } catch {}
  return 1;
})();

export const volume = signal<number>(initialVolume);
export const isMuted = signal<boolean>(false);

/** Episodes queued to play after the current one. Mirrored from the server queue. */
export const upNext = signal<EpisodeDto[]>([]);

/**
 * Warm the next couple of episodes whenever the tail changes.
 *
 * Placed here rather than in a component because the handover it smooths — one episode
 * ending and the next starting — happens whether or not anything is on screen.
 */
effect(() => {
  preloadUpcoming(upNext.value);
});

export const activeChapter = computed<Chapter | null>(() => {
  const list = chapters.value;
  if (list.length === 0) return null;
  const t = positionSec.value;
  let found: Chapter | null = null;
  for (const ch of list) {
    if (ch.startTime <= t) found = ch;
    else break;
  }
  return found;
});

export const progressFraction = computed(() =>
  durationSec.value > 0 ? Math.min(1, positionSec.value / durationSec.value) : 0,
);

// --------------------------------------------------------------------- audio element

let audio: HTMLAudioElement | null = null;
let lastSyncedAt = 0;
let lastSyncedPosition = 0;

/**
 * The episode the `<audio>` element is currently pointed at.
 *
 * Not the same question as "which episode is current": starting an episode while casting never
 * touches the element, so coming back off a cast session has to load it for real rather than
 * assume it is already there and seek into silence.
 */
let loadedFor: EpisodeDto['id'] | null = null;

/** How often position is persisted while playing. Every write hits D1's single writer. */
const SYNC_INTERVAL_MS = 20_000;
const SYNC_MIN_DELTA_SEC = 15;

export function attachAudio(el: HTMLAudioElement): void {
  if (audio === el) return;
  audio = el;

  el.volume = isMuted.value ? 0 : volume.value;
  el.muted = isMuted.value;

  el.addEventListener('play', () => {
    isPlaying.value = true;
    playbackError.value = null;
    updateMediaSessionPlaybackState();
  });
  el.addEventListener('pause', () => {
    isPlaying.value = false;
    updateMediaSessionPlaybackState();
    void syncProgress({ force: true });
  });
  el.addEventListener('waiting', () => {
    isBuffering.value = true;
  });
  el.addEventListener('playing', () => {
    isBuffering.value = false;
  });
  el.addEventListener('loadedmetadata', () => {
    if (Number.isFinite(el.duration)) durationSec.value = el.duration;
    updatePositionState();
  });
  el.addEventListener('durationchange', () => {
    if (Number.isFinite(el.duration)) durationSec.value = el.duration;
  });
  el.addEventListener('timeupdate', () => {
    positionSec.value = el.currentTime;
    void syncProgress({});
  });
  el.addEventListener('ratechange', () => {
    playbackRate.value = el.playbackRate;
    updatePositionState();
  });
  el.addEventListener('ended', () => {
    void handleEnded();
  });
  el.addEventListener('error', () => {
    isBuffering.value = false;
    isPlaying.value = false;
    playbackError.value =
      'This episode could not be played. The host may be offline or blocking playback.';
    // The element's buffer is broken; a retry on the same episode must re-point `src` and
    // reload rather than resume playing a source that has already failed.
    loadedFor = null;
  });

  // A discarded page never fires `pause`, so flush the position on teardown too.
  addEventListener('pagehide', flushProgressBeacon);
  addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flushProgressBeacon();
  });
}

// -------------------------------------------------------------------------- controls

export async function playEpisode(
  episode: EpisodeDto,
  opts: { startAt?: number; autoplay?: boolean } = {},
): Promise<void> {
  isLive.value = false;
  liveItem.value = null;

  const isCurrent = currentEpisode.value?.id === episode.id;
  if (!isCurrent) {
    batch(() => {
      currentEpisode.value = episode;
      positionSec.value = 0;
      durationSec.value = episode.durationSec ?? 0;
      chapters.value = [];
      playbackError.value = null;
    });
    setMediaSessionMetadata(episode);
    void loadChapters(episode);
  }

  if (isCasting()) {
    if (isCurrent) {
      if (opts.startAt !== undefined) await castSeek(opts.startAt);
      if (opts.autoplay !== false) await castPlay();
    } else {
      await sendToReceiver(
        episode,
        opts.startAt ?? episode.progress?.positionSec ?? 0,
        opts.autoplay !== false,
      );
    }
    return;
  }

  if (!audio) return;

  if (loadedFor !== episode.id) {
    // The Android shell refuses cleartext app-wide, so pointing <audio> at an http:// enclosure
    // there fails as net::ERR_CLEARTEXT_NOT_PERMITTED with no event this element ever sees —
    // checked here, before load(), because that failure is otherwise silent. A browser (and the
    // iOS PWA) has no such restriction, so this only applies inside the shell.
    if (hasNative.value && isInsecureEnclosureUrl(episode.enclosureUrl)) {
      isBuffering.value = false;
      isPlaying.value = false;
      playbackError.value = INSECURE_ENCLOSURE_MESSAGE;
      return;
    }

    // Point <audio> straight at a source, never proxied through the Worker: proxying breaks
    // Range requests (and therefore seeking), and an opaque cross-origin response makes
    // `currentTime` unsettable.
    audio.src = pickPlayableSource(audio, episode);
    audio.preload = 'metadata';
    audio.load();
    loadedFor = episode.id;

    // `positionSec` when the episode is already current: that is the handback off a cast
    // session, where the position the receiver reached beats the one the server last stored.
    const resumeAt =
      opts.startAt ?? (isCurrent ? positionSec.value : (episode.progress?.positionSec ?? 0));
    if (resumeAt > 0) seekTo(resumeAt);
  } else if (opts.startAt !== undefined) {
    seekTo(opts.startAt);
  }

  audio.playbackRate = playbackRate.value;

  if (opts.autoplay !== false) {
    try {
      await audio.play();
    } catch (err) {
      // Autoplay rejection is normal outside a user gesture; surface anything else.
      if ((err as Error)?.name !== 'NotAllowedError') {
        playbackError.value = `Playback failed: ${(err as Error).message}`;
      }
    }
  }
}

export async function playLive(item: LiveItemDto): Promise<void> {
  const syntheticEpisode: EpisodeDto = {
    id: -item.id,
    feedId: item.feedId,
    guid: `live-${item.id}`,
    title: item.title,
    feedTitle: item.feedTitle ?? item.feed?.title ?? 'Live Stream',
    feedImageUrl: item.feedImageUrl ?? item.feed?.imageUrl ?? null,
    imageUrl: item.imageUrl ?? item.feedImageUrl ?? item.feed?.imageUrl ?? null,
    enclosureUrl: item.enclosureUrl,
    pubDate: item.startTime,
    durationSec: 0,
    description: item.status,
    transcripts: [],
    link: null,
    enclosureType: item.enclosureType ?? 'audio/mpeg',
    enclosureLen: null,
    season: null,
    episodeNum: null,
    episodeType: 'full',
    explicit: false,
    chaptersUrl: null,
    soundbites: [],
    persons: [],
    alternateEnclosures: [],
    socialInteract: [],
    value: null,
  };

  batch(() => {
    isLive.value = true;
    liveItem.value = item;
    activeLiveChat.value = {
      feedId: item.feedId,
      guid: item.guid,
      title: item.title,
    };
    currentEpisode.value = syntheticEpisode;
    positionSec.value = 0;
    durationSec.value = Infinity;
    chapters.value = [];
    playbackError.value = null;
  });

  setMediaSessionMetadata(syntheticEpisode);

  if (!audio) return;

  loadedFor = syntheticEpisode.id;
  audio.src = item.enclosureUrl;
  audio.preload = 'none';
  audio.load();
  audio.playbackRate = playbackRate.value;

  try {
    await audio.play();
  } catch (err) {
    if ((err as Error)?.name !== 'NotAllowedError') {
      playbackError.value = `Playback failed: ${(err as Error).message}`;
    }
  }
}

export function togglePlay(): void {
  if (!currentEpisode.value) return;
  if (isCasting()) {
    void (isPlaying.value ? castPause() : castPlay());
    return;
  }
  if (!audio) return;
  if (audio.paused) {
    if (isLive.value && liveItem.value) {
      try {
        if (audio.seekable && audio.seekable.length > 0) {
          audio.currentTime = audio.seekable.end(audio.seekable.length - 1);
        } else {
          audio.src = liveItem.value.enclosureUrl;
          audio.load();
        }
      } catch {}
    }
    void audio.play().catch(() => {});
  } else {
    audio.pause();
  }
}

export function seekTo(seconds: number): void {
  const max = durationSec.value || audio?.duration || Number.MAX_SAFE_INTEGER;
  const clamped = Math.max(0, Math.min(seconds, max));

  if (isCasting()) {
    // Optimistic: the receiver's next frame is up to a second away, and a scrubber that snaps
    // back to where it was before catching up reads as a failed seek.
    positionSec.value = clamped;
    updatePositionState();
    void castSeek(clamped);
    return;
  }

  if (!audio) return;
  try {
    audio.currentTime = clamped;
    positionSec.value = clamped;
    updatePositionState();
  } catch {
    // Some hosts serve media that is briefly unseekable while metadata loads.
  }
}

export function skipForward(): void {
  seekTo(positionSec.value + (settings.value.skipForwardSec ?? 30));
}

export function skipBack(): void {
  seekTo(positionSec.value - (settings.value.skipBackSec ?? 15));
}

/** The fastest the `<audio>` element is offered. A receiver's own, lower ceiling is `cast.ts`'s. */
const LOCAL_MAX_RATE = 3;

/** Whichever player is live decides the ceiling. */
function clampRate(rate: number): number {
  return Math.max(0.5, Math.min(isCasting() ? CAST_MAX_RATE : LOCAL_MAX_RATE, rate));
}

export function setPlaybackRate(rate: number): void {
  const clamped = clampRate(rate);
  playbackRate.value = clamped;
  if (isCasting()) {
    void castSetRate(clamped);
    return;
  }
  if (audio) audio.playbackRate = clamped;
}

/** The speeds offered in the UI's speed picker, and stepped through by the `,`/`.` shortcuts. */
export const RATES = [0.8, 1, 1.2, 1.5, 1.8, 2, 2.5, 3];

/** Moves to the next (or, for a negative `direction`, previous) entry in [RATES]. */
export function stepPlaybackRate(direction: 1 | -1): void {
  const index = RATES.indexOf(playbackRate.value);
  // An off-list rate (there is no UI for setting one today, but nothing rules it out) steps
  // from its nearest neighbour rather than throwing the whole step away.
  const nearest = RATES.findIndex((r) => r >= playbackRate.value);
  const from = index === -1 ? (nearest === -1 ? RATES.length - 1 : nearest) : index;
  const next = RATES[Math.max(0, Math.min(RATES.length - 1, from + direction))];
  setPlaybackRate(next);
}

export function jumpToChapter(chapter: Chapter): void {
  seekTo(chapter.startTime);
  if (!isPlaying.value) togglePlay();
}

// ---------------------------------------------------------------------------- volume

export function setVolume(vol: number): void {
  const clamped = Math.max(0, Math.min(1, vol));
  volume.value = clamped;
  if (isMuted.value && clamped > 0) {
    isMuted.value = false;
  }
  if (audio) {
    audio.volume = isMuted.value ? 0 : clamped;
    audio.muted = isMuted.value;
  }
  try {
    localStorage.setItem('podcastie:volume', String(clamped));
  } catch {}
}

export function toggleMute(): void {
  const next = !isMuted.value;
  isMuted.value = next;
  if (audio) {
    audio.muted = next;
    audio.volume = next ? 0 : volume.value;
  }
}

export function adjustVolume(delta: number): void {
  if (isMuted.value) {
    isMuted.value = false;
  }
  setVolume(volume.value + delta);
}

// ---------------------------------------------------------------------- sleep timer

let sleepTimerHandle: number | undefined;

export function setSleepTimer(minutes: number | null, atEpisodeEnd = false): void {
  if (sleepTimerHandle !== undefined) {
    clearTimeout(sleepTimerHandle);
    sleepTimerHandle = undefined;
  }
  sleepTimerAtEpisodeEnd.value = atEpisodeEnd;

  if (atEpisodeEnd) {
    sleepTimerEndsAt.value = null;
    return;
  }
  if (minutes === null) {
    sleepTimerEndsAt.value = null;
    return;
  }

  const endsAt = Date.now() + minutes * 60_000;
  sleepTimerEndsAt.value = endsAt;
  sleepTimerHandle = setTimeout(() => {
    pausePlayback();
    sleepTimerEndsAt.value = null;
    sleepTimerHandle = undefined;
  }, minutes * 60_000) as unknown as number;
}

/** Pauses whichever player is live. The sleep timer must reach the TV too. */
function pausePlayback(): void {
  if (isCasting()) {
    void castPause();
    return;
  }
  audio?.pause();
}

// ------------------------------------------------------------------------ casting

/** True while a Chromecast session owns playback. False in every browser. */
export function isCasting(): boolean {
  return castConnected.value;
}

/**
 * The receiver fetches the audio itself, from a device that is not this phone, so it always gets
 * the publisher's URL — a downloaded episode lives on local storage nothing else can reach.
 */
function sendToReceiver(episode: EpisodeDto, startAt: number, autoplay: boolean): Promise<void> {
  finishedFor = null;

  // Clamped here rather than trusted from the signal: connecting mid-listen never passes through
  // `setPlaybackRate`, so a session at 3× would be sent as 3×, clamped to 2× by the receiver, and
  // leave the speed chip advertising a speed the TV is not playing at. Writing the clamped value
  // back is what keeps the chip honest — and it is why nothing mirrors `status.rate` in the other
  // direction, which would only flicker on the frames before the media loads.
  const rate = clampRate(playbackRate.value);
  playbackRate.value = rate;

  return castLoad({
    id: String(episode.id),
    title: episode.title,
    feedTitle: episode.feedTitle ?? '',
    imageUrl: episode.imageUrl,
    feedImageUrl: episode.feedImageUrl,
    enclosureUrl: episode.enclosureUrl,
    durationSec: episode.durationSec ?? 0,
    startAt,
    rate,
    autoplay,
  });
}

/** Tracks the connect/disconnect edges; the frames themselves carry no history. */
let wasCasting = false;

/**
 * The episode whose end has already been acted on. The receiver keeps reporting `finished` for as
 * long as it sits idle, and without this the queue would advance once per frame.
 */
let finishedFor: string | null = null;

onCastStatus((status) => {
  if (status.connected && !wasCasting) startCasting();
  else if (!status.connected && wasCasting) stopCastingLocally();
  wasCasting = status.connected;

  if (!status.connected) return;
  adoptReceiverEpisode(status);
  applyReceiverStatus(status);
});

/** One adoption at a time; frames arrive every second and the fetch takes longer than that. */
let adopting = false;

/**
 * Picks up an episode that is already on the receiver.
 *
 * A cast session outlives the page — the app can be killed and relaunched, or the WebView
 * reloaded, while the TV plays on — and the new document knows nothing about it. Only ever with
 * no current episode: once there is one, the receiver's id lags a load in flight by a frame or
 * two, and adopting it then would drag the listener back to what they just skipped.
 */
function adoptReceiverEpisode(status: CastStatus): void {
  if (adopting || currentEpisode.value || !status.episodeId) return;
  const id = Number(status.episodeId);
  if (!Number.isFinite(id)) return;

  adopting = true;
  void api
    .episode(id)
    .then(({ episode }) => {
      // Still nothing playing: a tap during the round trip wins over what the TV was doing.
      if (currentEpisode.value) return;
      batch(() => {
        currentEpisode.value = episode;
        positionSec.value = status.positionSec;
        durationSec.value = status.durationSec || (episode.durationSec ?? 0);
      });
      setMediaSessionMetadata(episode);
      void loadChapters(episode);
    })
    .catch(() => {
      // Offline, or the episode has since been removed. The transport keeps working from the
      // frames; only the artwork and title are missing.
    })
    .finally(() => {
      adopting = false;
    });
}

/** A session just came up: silence the phone and put what it was playing on the TV. */
function startCasting(): void {
  const episode = currentEpisode.value;
  const resumeAt = positionSec.value;
  const wasPlaying = isPlaying.value;

  // Pausing first also flushes the position, via the element's own `pause` handler.
  audio?.pause();
  syncNativePlaybackStop();
  if (!episode) return;
  void sendToReceiver(episode, resumeAt, wasPlaying);
}

/**
 * The session ended. Playback comes back to the phone where the receiver left off, and keeps
 * going if it was going — which is what "stop casting" means to someone still listening, and what
 * the Cast UX guidelines ask for.
 */
function stopCastingLocally(): void {
  const episode = currentEpisode.value;
  const resumeAt = positionSec.value;
  const wasPlaying = isPlaying.value;

  isBuffering.value = false;
  if (!episode) return;
  void playEpisode(episode, { startAt: resumeAt, autoplay: wasPlaying });
}

/** The receiver is the source of truth while it is playing; these signals just mirror it. */
function applyReceiverStatus(status: CastStatus): void {
  batch(() => {
    isPlaying.value = status.playing;
    isBuffering.value = status.buffering;
    if (status.durationSec > 0) durationSec.value = status.durationSec;
    // An idle receiver reports position 0. Taking that would drop the scrubber to the start the
    // moment an episode ends, before the queue has moved on.
    if (status.positionSec > 0 || status.playing) positionSec.value = status.positionSec;
    // Rate is deliberately not mirrored back: the receiver reports 1× on the frames between
    // connecting and the media loading, which would only make the speed chip flicker. Everything
    // sent to it is clamped by `sendToReceiver` and `setPlaybackRate` first, so the signal is
    // already what the receiver will honour.
  });
  updateMediaSessionPlaybackState();

  if (status.finished) {
    const id = currentEpisode.value?.id;
    if (id !== undefined && finishedFor !== String(id)) {
      finishedFor = String(id);
      void handleEnded();
    }
    return;
  }
  finishedFor = null;
  void syncProgress({});
}

// ------------------------------------------------------------------ queue handoff

async function handleEnded(): Promise<void> {
  isPlaying.value = false;
  const finished = currentEpisode.value;
  if (finished) {
    // Mirror the server-side dequeue locally: `upNext` is filtered below, but that is a
    // player-only derived list, not what the Queue view renders. Without this, a Queue view
    // already mounted when an episode finishes keeps showing it as still queued until
    // something else happens to trigger `refreshQueue()`.
    queueItems.value = queueItems.value.filter((i) => i.episode.id !== finished.id);

    // Save progress and dequeue in background asynchronously to prevent audio gaps and autoplay policy drops (#51)
    void Promise.allSettled([
      api.putProgress(finished.id, {
        position: durationSec.value || finished.durationSec || 0,
        duration: durationSec.value || undefined,
        completed: true,
        rate: playbackRate.value,
      }),
      api.dequeue(finished.id),
    ]);
  }

  if (sleepTimerAtEpisodeEnd.value) {
    sleepTimerAtEpisodeEnd.value = false;
    upNext.value = upNext.value.filter((e) => e.id !== finished?.id);
    syncNativePlaybackStop();
    return;
  }

  const [next, ...rest] = upNext.value.filter((e) => e.id !== finished?.id);
  upNext.value = rest;
  if (next) {
    await playEpisode(next);
  } else {
    syncNativePlaybackStop();
  }
}

export function playNext(): void {
  const [next, ...rest] = upNext.value;
  if (!next) return;
  upNext.value = rest;
  void playEpisode(next);
}

// -------------------------------------------------------------------- progress sync

interface SyncOptions {
  force?: boolean;
}

/**
 * Debounced position persistence.
 *
 * Writes are throttled by both time and distance moved: a listener who scrubs repeatedly
 * should not generate a write per scrub event.
 */
async function syncProgress({ force }: SyncOptions): Promise<void> {
  if (isLive.value) return;
  const episode = currentEpisode.value;
  if (!episode) return;

  const now = Date.now();
  const movedEnough = Math.abs(positionSec.value - lastSyncedPosition) >= SYNC_MIN_DELTA_SEC;
  const dueByTime = now - lastSyncedAt >= SYNC_INTERVAL_MS;
  if (!force && (!dueByTime || !movedEnough)) return;
  if (positionSec.value <= 0) return;

  lastSyncedAt = now;
  lastSyncedPosition = positionSec.value;

  await api
    .putProgress(episode.id, {
      position: positionSec.value,
      duration: durationSec.value || undefined,
      rate: playbackRate.value,
    })
    .catch(() => {
      // A dropped progress write is not worth interrupting playback over; the next tick
      // or the unload beacon will carry the position.
    });
}

function flushProgressBeacon(): void {
  if (isLive.value) return;
  const episode = currentEpisode.value;
  if (!episode || positionSec.value <= 0) return;
  beaconProgress(episode.id, {
    position: positionSec.value,
    duration: durationSec.value || undefined,
    rate: playbackRate.value,
  });
}

async function loadChapters(episode: EpisodeDto): Promise<void> {
  if (!episode.chaptersUrl) {
    chapters.value = [];
    return;
  }
  try {
    const { chapters: list } = await api.chapters(episode.id);
    // Ignore a late response for an episode the listener has already moved past.
    if (currentEpisode.value?.id === episode.id) chapters.value = list;
  } catch {
    chapters.value = [];
  }
}

// ------------------------------------------------------------------- Media Session & Native Bridge

let mediaControlsBound = false;

export function initNativeMediaControls(): void {
  whenNative((_native) => {
    if (mediaControlsBound) return;
    mediaControlsBound = true;
    onNativeEvent((event) => {
      if (event?.type === 'mediaSessionAction') {
        switch (event.action) {
          case 'play':
            if (!isPlaying.value) togglePlay();
            break;
          case 'pause':
            if (isPlaying.value) togglePlay();
            break;
          case 'skipForward':
            skipForward();
            break;
          case 'skipBack':
            skipBack();
            break;
          case 'seekTo':
            if (typeof event.positionSec === 'number') seekTo(event.positionSec);
            break;
          case 'next':
            playNext();
            break;
          case 'stop':
            if (isPlaying.value) togglePlay();
            syncNativePlaybackStop();
            break;
        }
      }
    });
  });
}

export function resetNativeMediaControls(): void {
  mediaControlsBound = false;
}

function syncNativeMetadata(episode: EpisodeDto): void {
  whenNative((native) => {
    if (!native.mediaMetadata) return;
    void native.mediaMetadata({
      title: episode.title,
      feedTitle: episode.feedTitle ?? '',
      imageUrl: episode.imageUrl ?? episode.feedImageUrl ?? null,
      durationSec: durationSec.value || episode.durationSec || 0,
      episodeId: episode.id,
    });
  });
}

function syncNativePlaybackState(): void {
  if (isCasting()) return;
  whenNative((native) => {
    if (!native.mediaPlaybackState) return;
    void native.mediaPlaybackState({
      playing: isPlaying.value,
      positionSec: positionSec.value,
      durationSec: durationSec.value,
      rate: playbackRate.value,
      buffering: isBuffering.value,
    });
  });
}

function syncNativePlaybackStop(): void {
  whenNative((native) => {
    if (!native.mediaPlaybackStop) return;
    void native.mediaPlaybackStop();
  });
}

/**
 * Media Session is progressive enhancement.
 *
 * It is solid on Android Chrome and desktop; on iOS Safari support is partial and
 * historically buggy (artwork sizing, control fidelity), so nothing here is load-bearing.
 */
function setMediaSessionMetadata(episode: EpisodeDto): void {
  initNativeMediaControls();
  syncNativeMetadata(episode);
  syncNativePlaybackState();

  if (!('mediaSession' in navigator)) return;

  const art = episode.imageUrl ?? episode.feedImageUrl ?? undefined;
  navigator.mediaSession.metadata = new MediaMetadata({
    title: episode.title,
    artist: episode.feedTitle ?? '',
    album: isLive.value ? '🔴 Live Stream' : (episode.feedTitle ?? ''),
    // Offer several sizes — iOS is particular about which artwork it will accept.
    artwork: art
      ? [
          { src: art, sizes: '96x96', type: 'image/jpeg' },
          { src: art, sizes: '192x192', type: 'image/jpeg' },
          { src: art, sizes: '512x512', type: 'image/jpeg' },
        ]
      : [],
  });

  const handlers: [MediaSessionAction, MediaSessionActionHandler][] = [
    ['play', () => togglePlay()],
    ['pause', () => togglePlay()],
    ['seekbackward', () => skipBack()],
    ['seekforward', () => skipForward()],
    ['previoustrack', () => seekTo(0)],
    ['nexttrack', () => playNext()],
    [
      'seekto',
      (details) => {
        if (typeof details.seekTime === 'number') seekTo(details.seekTime);
      },
    ],
  ];
  for (const [action, handler] of handlers) {
    try {
      navigator.mediaSession.setActionHandler(action, handler);
    } catch {
      // Unsupported actions throw rather than no-op in some browsers.
    }
  }
}

function updateMediaSessionPlaybackState(): void {
  syncNativePlaybackState();
  if (!('mediaSession' in navigator)) return;
  navigator.mediaSession.playbackState = isPlaying.value ? 'playing' : 'paused';
  updatePositionState();
}

function updatePositionState(): void {
  syncNativePlaybackState();
  if (!('mediaSession' in navigator) || !navigator.mediaSession.setPositionState) return;
  const duration = durationSec.value;
  if (duration <= 0) return;
  try {
    if (isLive.value || duration === Infinity) {
      navigator.mediaSession.setPositionState({
        duration: Infinity,
        playbackRate: playbackRate.value,
        position: positionSec.value,
      });
    } else if (Number.isFinite(duration)) {
      navigator.mediaSession.setPositionState({
        duration,
        playbackRate: playbackRate.value,
        position: Math.min(positionSec.value, duration),
      });
    }
  } catch {
    // Throws if position exceeds duration mid-update, or if Infinity not supported; harmless.
  }
}

// ------------------------------------------------------------------------- helpers

export function formatTime(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined || !Number.isFinite(seconds) || seconds < 0) {
    return '--:--';
  }
  const total = Math.floor(seconds);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const pad = (n: number) => String(n).padStart(2, '0');
  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${m}:${pad(s)}`;
}

export function formatDuration(msOrSec: number): string {
  // If value is in milliseconds (> 100,000s = ~27 hours, typical for Date.now() diffs), convert to seconds
  const sec = msOrSec > 100_000 ? Math.floor(msOrSec / 1000) : Math.floor(msOrSec);
  return formatTime(sec);
}

export function formatRemaining(position: number, duration: number): string {
  if (!duration) return '';
  return `${formatTime(Math.max(0, duration - position))} left`;
}

export interface PlayableSource {
  url: string;
  type: string;
}

export function getPlayableSources(episode: EpisodeDto): PlayableSource[] {
  const candidates: PlayableSource[] = [];

  if (episode.alternateEnclosures) {
    for (const alt of episode.alternateEnclosures) {
      const altType = alt.type ?? '';
      for (const src of alt.sources ?? []) {
        if (src.uri) {
          candidates.push({
            url: src.uri,
            type: src.contentType || altType || 'audio/mpeg',
          });
        }
      }
    }
  }

  if (episode.enclosureUrl) {
    candidates.push({
      url: episode.enclosureUrl,
      type: episode.enclosureType || 'audio/mpeg',
    });
  }

  const seen = new Set<string>();
  const unique: PlayableSource[] = [];
  for (const item of candidates) {
    if (!seen.has(item.url)) {
      seen.add(item.url);
      unique.push(item);
    }
  }

  return unique.sort((a, b) => {
    const aAudio = a.type.startsWith('audio/');
    const bAudio = b.type.startsWith('audio/');
    if (aAudio && !bAudio) return -1;
    if (!aAudio && bAudio) return 1;
    return 0;
  });
}

/**
 * The first candidate the element itself reports it can play, preferring `canPlayType`'s
 * "probably" over its noncommittal "maybe" — this is how an alternate Opus/AAC enclosure gets
 * used ahead of the fallback MP3 one, on a browser that actually supports it. Untyped browser
 * quirks fall back to the primary enclosure, same as before this existed.
 */
export function pickPlayableSource(el: HTMLAudioElement, episode: EpisodeDto): string {
  const sources = getPlayableSources(episode);
  const probably = sources.find((s) => el.canPlayType(s.type) === 'probably');
  if (probably) return probably.url;
  const maybe = sources.find((s) => el.canPlayType(s.type) === 'maybe');
  if (maybe) return maybe.url;
  return episode.enclosureUrl;
}

